import { z } from 'zod/v4';

/**
 * Sort columns this endpoint accepts. Restricted to columns actually
 * projected by the `companies_view` subquery in
 * `core/db/companies/list-available-for-user.ts` (`SORT_COLUMNS`) — unlike
 * the general admin companies list, this query never joins relationship
 * types, collaboration status, or platform features, so sort keys tied to
 * those (e.g. `relationshipType`, `connectionType`) are not reachable here.
 */
export const AdminAvailableCompaniesSortColumns = [
  'name',
  'officialName',
  'country',
  'countryName',
  'updatedAt',
  'createdAt',
  'facilityProcesses',
  'city',
  'companySize',
  'companyType',
  'isDemoCompany',
] as const;

export type AdminAvailableCompaniesSortColumn =
  (typeof AdminAvailableCompaniesSortColumns)[number];

const sortParamSchema = z
  .string()
  .regex(/^[a-zA-Z]+:(ASC|DESC|asc|desc)$/)
  .optional();

export const AdminAvailableCompaniesParamsSchema = z.object({
  userId: z.string().min(1),
});

export type AdminAvailableCompaniesParams = z.infer<
  typeof AdminAvailableCompaniesParamsSchema
>;

export const AdminAvailableCompaniesQuerySchema = z.object({
  q: z.string().min(1).optional(),
  sort: sortParamSchema,
});

export type AdminAvailableCompaniesQuery = z.infer<
  typeof AdminAvailableCompaniesQuerySchema
>;

/**
 * Row shape returned by the available-companies-for-user admin endpoint.
 * `looseObject` to stay permissive like the sibling admin list schemas.
 *
 * Field reads in the FE (must stay populated): id, officialName.
 */
export const AdminAvailableCompanyRowSchema = z.looseObject({
  id: z.string(),
  officialName: z.string(),
  name: z.string(),
  country: z.string(),
  countryName: z.string(),
  featuredImageFileId: z.string(),
  featuredImageFileUrl: z.string().nullable(),
  originalFeaturedImageFileUrl: z.string(),
  videoFileUrl: z.null(),
  imageList: z.array(z.unknown()),
  geojson: z.string().nullable(),
  facilityProcesses: z.string().nullable(),
  // Postgres timestamp columns come back as JS `Date` objects, not
  // pre-stringified — `z.coerce.date()` matches the sibling schemas
  // (company.ts, admin-list.ts, etc.) that also expose this column.
  updatedAt: z.coerce.date().nullable(),
});

export type AdminAvailableCompanyRow = z.infer<
  typeof AdminAvailableCompanyRowSchema
>;

// Legacy handler sent `reply.code(200).send(result)` — a bare array, not the
// `{data, meta}` envelope other admin list endpoints use. The only FE
// consumer reads the response body directly as the array, so this is kept
// unwrapped to stay backward compatible.
export const AdminAvailableCompaniesResponseSchema = z.array(
  AdminAvailableCompanyRowSchema,
);

export type AdminAvailableCompaniesResponse = z.infer<
  typeof AdminAvailableCompaniesResponseSchema
>;
